import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deletePushSubscription,
  getPushCapability,
  pushKeys,
  savePushSubscription,
} from './notificationsApi.ts';
import {
  detectPushSupport,
  readSubscription,
  requestPermission,
  subscribe,
  unsubscribe,
  type PushBlocker,
} from './pushSubscription.ts';

export interface PushNotificationState {
  /** Whether this deployment can send at all. False while loading, and on a server with no keys. */
  available: boolean;
  /** Why it cannot be offered on this device, or null when it can. */
  blocker: PushBlocker | null;
  /** Whether *this device* is registered. */
  subscribed: boolean;
  isLoading: boolean;
  isBusy: boolean;
  error: Error | null;
  enable: () => void;
  disable: () => void;
}

export function usePushNotifications(): PushNotificationState {
  const queryClient = useQueryClient();

  // Read on every render rather than memoised, because it is a synchronous look at browser
  // state that genuinely changes underneath us: refusing the permission dialog flips
  // `Notification.permission` from "default" to "denied" while this component is mounted,
  // and a value captured at mount would leave the toggle offering something the browser
  // will now silently refuse.
  const support = detectPushSupport();

  const capability = useQuery({
    queryKey: pushKeys.capability,
    queryFn: async () => {
      // Only meaningful where there is a PushManager to ask. Everywhere else the endpoint
      // is null and the server answers about availability alone.
      const existing = support.blocker === null ? await readSubscription() : null;

      const answer = await getPushCapability(existing?.endpoint ?? null);

      // Self-healing, and the reason this is not a plain GET. A browser can replace a push
      // subscription on its own — after a long idle period, or when it decides the old one
      // is stale — and the only event that reports it (`pushsubscriptionchange`) fires in
      // the service worker, which has no bearer token and so cannot tell our API. The
      // symptom is a device that shows notifications as on and never receives one again.
      // Re-registering whatever the browser currently holds costs one idempotent PUT and
      // fixes it the next time anybody opens this page.
      if (answer.available && existing !== null && !answer.subscribed) {
        await savePushSubscription(existing);
        return { ...answer, subscribed: true };
      }

      return answer;
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: pushKeys.capability });

  const enable = useMutation({
    // The already-started permission dialog, handed in from the tap rather than opened
    // here — see requestPermission. React Query puts at least one await in front of a
    // mutationFn, and on iOS a permission prompt that is not plainly a response to direct
    // interaction is refused with no dialog at all.
    mutationFn: async (permission: Promise<NotificationPermission>) => {
      const publicKey = capability.data?.publicKey;

      if (!publicKey) {
        throw new Error('This server is not set up to send notifications.');
      }

      await savePushSubscription(await subscribe(publicKey, permission));
    },
    // Runs whether it succeeded or not: a refused permission dialog is a real change to
    // what this device can be offered, and the refetch is what redraws the section as
    // blocked rather than leaving a toggle that does nothing.
    onSettled: invalidate,
  });

  const disable = useMutation({
    mutationFn: async () => {
      const endpoint = await unsubscribe();

      if (endpoint !== null) {
        await deletePushSubscription(endpoint);
      }
    },
    onSettled: invalidate,
  });

  return {
    available: capability.data?.available ?? false,
    blocker: support.blocker,
    subscribed: capability.data?.subscribed ?? false,
    isLoading: capability.isPending,
    isBusy: enable.isPending || disable.isPending,
    error: enable.error ?? disable.error,
    // requestPermission() is called here, in the click handler's own turn, and only its
    // promise goes into the mutation. Do not "tidy" this by moving the call inside the
    // mutationFn: that is exactly the change that makes the switch do nothing on an
    // iPhone and work fine everywhere the developer is likely to be testing.
    enable: () => enable.mutate(requestPermission()),
    disable: () => disable.mutate(),
  };
}
